import { useEffect, useMemo, useState } from "react";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { format, isValid, parse } from "date-fns";
import { de } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { Formik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { API_BASE_URL } from "../../config";
import useToast from "../../components/useToast";
import AppButton from "../../design-system/components/AppButton";
import AppChip from "../../design-system/components/AppChip";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppDivider from "../../design-system/components/AppDivider";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppInput from "../../design-system/components/AppInput";
import AppSelect from "../../design-system/components/AppSelect";
import CountryFlag from "../../design-system/components/CountryFlag";
import AppText from "../../design-system/components/AppText";
import AppTooltip from "../../design-system/components/AppTooltip";
import { IconInfo } from "../../design-system/icons";
import { canAssignCompanyAdmin, formatAccessLevel } from "../../utils/accessLevels";
import "../../i18n";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./invite.css";

const todayKey = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(rawValue)) return rawValue;

  const isoValue = rawValue.slice(0, 10);
  const parsedDate = parse(isoValue, "yyyy-MM-dd", new Date());
  return isValid(parsedDate) && format(parsedDate, "yyyy-MM-dd") === isoValue
    ? format(parsedDate, "dd.MM.yyyy")
    : rawValue;
};

const formatDateForApi = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parsedDate = parse(rawValue, "dd.MM.yyyy", new Date());
  return isValid(parsedDate) && format(parsedDate, "dd.MM.yyyy") === rawValue
    ? format(parsedDate, "yyyy-MM-dd")
    : null;
};

// Der Kalender arbeitet mit Date-Objekten, das Formular weiter mit
// "dd.MM.yyyy" -- so bleiben Validierung und Payload unveraendert. Eine
// unvollstaendige Eingabe wird als ungueltiger Text gehalten, damit die
// Formatpruefung greift und das Feld die Tipp-Eingabe nicht verliert.
const toPickerDate = (value) => (value ? parse(value, "dd.MM.yyyy", new Date()) : null);
const fromPickerDate = (date) => {
  if (!date) return "";
  return isValid(date) ? format(date, "dd.MM.yyyy") : "invalid";
};

const countryOptions = getCountries().map((code) => ({
  value: code,
  label: `${code} +${getCountryCallingCode(code)}`,
  displayLabel: `+${getCountryCallingCode(code)}`,
  leadingVisual: <CountryFlag countryCode={code} />,
}));

const backend00ToE164 = (value) => {
  if (!value) return "";
  const normalizedValue = String(value).trim().replace(/\s+/g, "");
  return normalizedValue.startsWith("00") ? `+${normalizedValue.slice(2)}` : normalizedValue;
};

const buildPhoneHint = (country = "CH") => {
  try {
    const example = getExampleNumber(country, examples);
    if (!example) {
      const callingCode = getCountryCallingCode(country);
      return { placeholder: `+${callingCode} …`, hint: `+${callingCode} …` };
    }
    return {
      // Im Feld steht bewusst nur die nationale Schreibweise (079 123 45 67):
      // kurz, vertraut und ohne zusaetzlichen Erklaertext daneben.
      placeholder: example.formatNational(),
      hint: `${example.formatInternational()} / ${example.formatNational()}`,
    };
  } catch {
    const callingCode = getCountryCallingCode(country);
    return { placeholder: `+${callingCode} …`, hint: `+${callingCode} …` };
  }
};

const hasOnlyAllowedPhoneChars = (value) => {
  if (!value) return true;
  if (!/^[0-9+().\-\s]*$/.test(value)) return false;

  const plusCount = (value.match(/\+/g) || []).length;
  return plusCount <= 1 && (plusCount === 0 || value.trim().startsWith("+"));
};

const normalizeToE164 = (rawValue, defaultCountry = "CH") => {
  if (!rawValue || !hasOnlyAllowedPhoneChars(rawValue)) return null;

  let value = String(rawValue).trim().replace(/[^\d+]/g, "");
  if (value.startsWith("00")) value = `+${value.slice(2)}`;

  try {
    const phoneNumber = parsePhoneNumberFromString(value, defaultCountry);
    return phoneNumber?.isValid() ? phoneNumber.number : null;
  } catch {
    return null;
  }
};

const makeInviteSchema = (t) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t("registration.invalid_email"))
      .test("domain-structure", t("registration.invalid_email_domain"), (value) => {
        if (!value) return true;
        const parts = value.split("@");
        if (parts.length !== 2) return false;
        const domain = parts[1];
        if (!domain || domain.length > 255 || domain.includes("..")) return false;
        const labels = domain.split(".");
        if (labels.length < 2 || !/^[A-Za-z]{2,24}$/.test(labels.at(-1))) return false;
        return labels.every(
          (label) => label.length > 0 && label.length <= 63 && !label.startsWith("-") && !label.endsWith("-")
        );
      })
      .required(t("registration.required")),
    first_name: yup.string().trim().required(t("registration.required")),
    last_name: yup.string().trim().required(t("registration.required")),
    company_name: yup.string(),
    employment: yup.string().required(t("registration.required")),
    entry_date: yup
      .string()
      .required(t("registration.required"))
      .test("entry-date-format", "Bitte Datum im Format dd.mm.yyyy eingeben.", (value) => !value || formatDateForApi(value) !== null),
    employment_level: yup
      .number()
      .min(0, t("registration.invalid"))
      .max(1, t("registration.invalid"))
      .required(t("registration.required")),
    access_level: yup.string().required(t("registration.required")),
    availability_mode: yup.string().oneOf(["absence", "availability"]),
    selected_location_ids: yup.array().of(yup.number()),
    primary_location_id: yup.number().nullable(),
    selected_department_ids: yup
      .array()
      .of(yup.number())
      .min(1, t("registration.required")),
    phone_country: yup.string().nullable().default("CH"),
    phone_number: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .test("phone-allowed", t("registration.phone_number_invalid"), (value) => !value || hasOnlyAllowedPhoneChars(value))
      .test("phone-valid", t("registration.phone_number_invalid"), function validatePhone(value) {
        return !value || Boolean(normalizeToE164(value, this.parent.phone_country || "CH"));
      })
      .notRequired(),
  });

const InviteDialog = ({
  open = false,
  onClose,
  onInvited,
}) => {
  const { t } = useTranslation();
  const { success, error: toastError } = useToast();
  const [inviteData, setInviteData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const token = localStorage.getItem("session_token");
  const validationSchema = useMemo(() => makeInviteSchema(t), [t]);

  useEffect(() => {
    const fetchInvite = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/web/invite`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInviteData(response.data || {});
      } catch (_error) {
        // Submission errors remain user-facing through the existing toast flow.
      } finally {
        setIsLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const currentLocationId = Number(inviteData.current_location_id || 0);
  const allDepartmentOptions = inviteData.department_options || [];
  const locationList = inviteData.locations || [];

  // Die auswaehlbaren Abteilungen haengen an den gewaehlten Standorten; ohne
  // Auswahl bleibt es beim aktuell aktiven Standort.
  const resolveLocationIds = (selectedLocationIds) => {
    const assigned = (selectedLocationIds || []).map(Number).filter(Boolean);
    if (assigned.length) return assigned;
    return currentLocationId ? [currentLocationId] : [];
  };

  // Der Standard-Standort ist der Einstiegsstandort der neuen Person in Web und
  // App. Er muss immer einer der zugeordneten Standorte sein — sonst greift der
  // erste gewaehlte.
  const resolvePrimaryLocationId = (selectedLocationIds, primaryLocationId) => {
    const assigned = (selectedLocationIds || []).map(Number).filter(Boolean);
    if (!assigned.length) return "";
    return assigned.includes(Number(primaryLocationId)) ? Number(primaryLocationId) : assigned[0];
  };

  // Bei mehreren Standorten wird nach Standort gruppiert, damit gleichnamige
  // Abteilungen unterscheidbar bleiben.
  const buildDepartmentSelectOptions = (selectedLocationIds) => {
    const locationIds = resolveLocationIds(selectedLocationIds);
    const allowed = new Set(locationIds);
    const visible = locationIds.length
      ? allDepartmentOptions.filter((department) => allowed.has(Number(department.location_id)))
      : allDepartmentOptions;
    const toOption = (department) => ({
      value: Number(department.id),
      label: department.name,
      displayLabel: department.name,
    });

    if (locationIds.length <= 1) {
      return visible.map(toOption);
    }

    return locationIds
      .map((locationId) => {
        const locationName = locationList.find((location) => Number(location.id) === locationId)?.name
          || "Ohne Standort";
        const options = visible
          .filter((department) => Number(department.location_id) === locationId)
          .map(toOption);
        return options.length ? { key: String(locationId), label: locationName, options } : null;
      })
      .filter(Boolean);
  };
  const canManageLocations = (
    canAssignCompanyAdmin(inviteData.current_access_level)
    || inviteData.can_assign_company_admin
  );
  const accessLevelOptions = canManageLocations
    ? ["User", "Admin", "company_admin"]
    : ["User", "Admin"];

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsLoading(true);
    try {
      const entryDate = formatDateForApi(values.entry_date);
      if (entryDate === null) {
        toastError("Bitte Datum im Format dd.mm.yyyy eingeben.");
        return;
      }

      const phoneE164 = normalizeToE164(values.phone_number, values.phone_country || "CH");
      const payload = {
        ...values,
        entry_date: entryDate || null,
        phone_number: phoneE164 ? phoneE164.replace(/^\+/, "00") : "",
        employment_level: Number(values.employment_level),
        selected_department_ids: Array.isArray(values.selected_department_ids)
          ? values.selected_department_ids
          : [],
        primary_location_id:
          resolvePrimaryLocationId(values.selected_location_ids, values.primary_location_id) || null,
      };

      const response = await axios.post(`${API_BASE_URL}/api/web/invite`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response?.data?.mail_status === "FAILED") {
        toastError(
          t("registration.mail_failed_friendly")
          || "Mitarbeiter wurde erfasst, aber die E-Mail konnte nicht zugestellt werden."
        );
      } else {
        success(t("registration.success_invite") || "Einladung erfolgreich verschickt.");
      }

      resetForm({
        values: {
          email: "",
          company_name: inviteData.company_name || values.company_name || "",
          phone_country: "CH",
          phone_number: "",
          first_name: "",
          last_name: "",
          entry_date: formatDateForDisplay(inviteData.entry_date || todayKey()),
          employment: "Perm",
          employment_level: 1,
          access_level: "User",
          availability_mode: "absence",
          selected_location_ids: [],
          primary_location_id: "",
          selected_department_ids: [],
        },
      });
      onInvited?.();
      onClose?.();
    } catch (error) {
      const status = error?.response?.status;
      const reason = error?.response?.data?.reason;
      if (status === 409 && reason === "EMAIL_EXISTS") {
        toastError(t("registration.email_exist") || "E-Mail existiert bereits.");
      } else if (status === 409 && reason === "PHONE_EXISTS") {
        toastError(t("registration.phone_exist") || "Telefonnummer existiert bereits.");
      } else {
        toastError(
          error?.response?.data?.message
          || error?.response?.data?.error
          || t("notification.error")
          || "Es ist ein Fehler aufgetreten."
        );
      }
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
        onSubmit={handleFormSubmit}
        enableReinitialize
        validateOnMount
        initialValues={{
          email: inviteData.email || "",
          company_name: inviteData.company_name || "",
          phone_country: "CH",
          phone_number: backend00ToE164(inviteData.phone_number || ""),
          first_name: inviteData.first_name || "",
          last_name: inviteData.last_name || "",
          entry_date: formatDateForDisplay(inviteData.entry_date || todayKey()),
          employment: inviteData.employment || "Perm",
          employment_level: typeof inviteData.employment_level === "number" ? inviteData.employment_level : 1,
          access_level: inviteData.access_level || "User",
          availability_mode: inviteData.availability_mode || "absence",
          selected_location_ids: [],
          primary_location_id: "",
          selected_department_ids: [],
        }}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          handleSubmit,
          setFieldTouched,
          isSubmitting,
          submitForm,
          validateForm,
          setTouched,
        }) => {
          const phoneCountry = values.phone_country || "CH";
          // Nur ein schlichtes Beispiel im Feld – kein zusaetzlicher Hinweistext.
          const { placeholder } = buildPhoneHint(phoneCountry);
          const onSaveClick = async () => {
            const validationErrors = await validateForm();
            if (Object.keys(validationErrors || {}).length > 0) {
              setTouched(
                {
                  email: true,
                  first_name: true,
                  last_name: true,
                  access_level: true,
                  employment: true,
                  employment_level: true,
                  availability_mode: true,
                  selected_department_ids: true,
                  phone_number: true,
                  entry_date: true,
                },
                true
              );
              toastError(t("registration.required") || "Bitte alle Pflichtfelder ausfüllen.");
              return;
            }
            await submitForm();
          };

          return (
            <AppDialog
              open={open}
              onClose={isSubmitting ? undefined : onClose}
              className="invite-dialog"
            >
              <AppDialogHeader
                title={t("registration.invite_title") || "Neue Mitarbeitende"}
                visual={<PersonAddAltOutlinedIcon />}
              />
              <AppDialogBody className="invite-dialog__body">
                <form className="invite-form" onSubmit={handleSubmit} noValidate>
                    <section className="invite-section" aria-labelledby="invite-section-personal">
                      <div className="invite-section__heading">
                        <AppChip size="sm" variant="primary" appearance="outlined">1</AppChip>
                        <AppText as="h2" variant="heading-3" id="invite-section-personal">
                          Persönliche Daten
                        </AppText>
                      </div>
                      <AppFieldGrid columns={2} size="md">
                        <AppField
                          label={t("registration.first_name")}
                          required
                          error={touched.first_name ? errors.first_name : undefined}
                        >
                          <AppInput
                            name="first_name"
                            value={values.first_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={Boolean(touched.first_name && errors.first_name)}
                          />
                        </AppField>
                        <AppField
                          label={t("registration.last_name")}
                          required
                          error={touched.last_name ? errors.last_name : undefined}
                        >
                          <AppInput
                            name="last_name"
                            value={values.last_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={Boolean(touched.last_name && errors.last_name)}
                          />
                        </AppField>
                        <AppField
                          label={t("registration.email")}
                          required
                          error={touched.email ? errors.email : undefined}
                        >
                          <AppInput
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={Boolean(touched.email && errors.email)}
                          />
                        </AppField>
                        <AppField
                          label={t("registration.phone")}
                          error={touched.phone_number ? errors.phone_number : undefined}
                        >
                          <div className="invite-phone-row">
                            <AppSelect
                              value={phoneCountry}
                              searchable
                              searchPlaceholder="Land suchen"
                              options={countryOptions}
                              onChange={(event) => setFieldValue("phone_country", event.target.value)}
                              aria-label={t("registration.country") || "Land"}
                            />
                            <AppInput
                              name="phone_number"
                              value={values.phone_number}
                              onChange={handleChange}
                              onBlur={(event) => {
                                const e164 = normalizeToE164(values.phone_number, phoneCountry);
                                if (e164) {
                                  const parsedPhone = parsePhoneNumberFromString(e164);
                                  if (parsedPhone) setFieldValue("phone_number", parsedPhone.formatInternational());
                                }
                                handleBlur(event);
                              }}
                              placeholder={placeholder}
                              invalid={Boolean(touched.phone_number && errors.phone_number)}
                            />
                          </div>
                        </AppField>
                        <AppField
                          label="Eintrittsdatum"
                          required
                          span={2}
                          error={touched.entry_date ? errors.entry_date : undefined}
                        >
                          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                            <DatePicker
                              format="dd.MM.yyyy"
                              value={toPickerDate(values.entry_date)}
                              onChange={(date) => setFieldValue("entry_date", fromPickerDate(date))}
                              onClose={() => setFieldTouched("entry_date", true, false)}
                              slotProps={{
                                textField: {
                                  name: "entry_date",
                                  size: "small",
                                  fullWidth: true,
                                  error: Boolean(touched.entry_date && errors.entry_date),
                                  onBlur: () => setFieldTouched("entry_date", true),
                                  inputProps: { "aria-label": "Eintrittsdatum" },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </AppField>
                      </AppFieldGrid>
                    </section>

                    <AppDivider spacing="lg" />

                    <section className="invite-section" aria-labelledby="invite-section-employment">
                      <div className="invite-section__heading">
                        <AppChip size="sm" variant="primary" appearance="outlined">2</AppChip>
                        <AppText as="h2" variant="heading-3" id="invite-section-employment">
                          Beschäftigung und Zugriff
                        </AppText>
                      </div>
                        <AppFieldGrid columns={2} size="md">
                          <AppField
                            label={(
                              <span className="invite-field-label-with-info">
                                {t("registration.employment")}
                                <AppTooltip content={t("registration.employment_info")}>
                                  <IconInfo aria-label={t("registration.employment_info_aria")} />
                                </AppTooltip>
                              </span>
                            )}
                            required
                          >
                            <AppSelect
                              name="employment"
                              value={values.employment}
                              onChange={(event) => setFieldValue("employment", event.target.value)}
                              options={[
                                { value: "Perm", label: t("registration.full_time") },
                                { value: "Temp", label: t("registration.part_time") },
                              ]}
                            />
                          </AppField>
                          <AppField label={t("registration.employment_level")} required>
                            <AppSelect
                              name="employment_level"
                              value={values.employment_level}
                              onChange={(event) => setFieldValue("employment_level", Number(event.target.value))}
                              options={[1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1].map((value) => ({
                                value,
                                label: `${Math.round(value * 100)}%`,
                              }))}
                            />
                          </AppField>
                          <AppField
                            label={(
                              <span className="invite-field-label-with-info">
                                {t("registration.access_level")}
                                <AppTooltip
                                  content={t(
                                    canManageLocations
                                      ? "registration.access_level_info_with_key_user"
                                      : "registration.access_level_info"
                                  )}
                                >
                                  <IconInfo aria-label={t("registration.access_level_info_aria")} />
                                </AppTooltip>
                              </span>
                            )}
                            required
                          >
                            <AppSelect
                              name="access_level"
                              value={values.access_level}
                              onChange={(event) => setFieldValue("access_level", event.target.value)}
                              options={accessLevelOptions.map((value) => ({
                                value,
                                label: formatAccessLevel(value),
                              }))}
                            />
                          </AppField>
                          <AppField
                            label={(
                              <span className="invite-field-label-with-info">
                                {t("availability.user_settings.mode_title")}
                                <AppTooltip content={t("availability.user_settings.mode_info")}>
                                  <IconInfo aria-label={t("availability.user_settings.mode_info_aria")} />
                                </AppTooltip>
                              </span>
                            )}
                          >
                            <AppSelect
                              name="availability_mode"
                              value={values.availability_mode}
                              onChange={(event) => setFieldValue("availability_mode", event.target.value)}
                              options={[
                                {
                                  value: "absence",
                                  label: t("availability.user_settings.absence"),
                                },
                                {
                                  value: "availability",
                                  label: t("availability.user_settings.availability"),
                                },
                              ]}
                            />
                          </AppField>
                        </AppFieldGrid>
                    </section>

                    <AppDivider spacing="lg" />

                    <section className="invite-section" aria-labelledby="invite-section-assignments">
                      <div className="invite-section__heading">
                        <AppChip size="sm" variant="primary" appearance="outlined">3</AppChip>
                        <AppText as="h2" variant="heading-3" id="invite-section-assignments">
                          Standort und Abteilungen
                        </AppText>
                      </div>
                        <AppFieldGrid columns={2} size="md">
                          {locationList.length > 1 ? (
                            <AppField
                              label="Standorte"
                              help={!canManageLocations
                                ? "Standorte können nur durch Key User geändert werden."
                                : undefined}
                            >
                              <AppSelect
                                multiple
                                value={values.selected_location_ids}
                                disabled={!canManageLocations}
                                placeholder="Aktueller Standort"
                                onChange={(event) => {
                                  const nextLocationIds = event.target.value.map(Number);
                                  setFieldValue("selected_location_ids", nextLocationIds);
                                  setFieldValue(
                                    "primary_location_id",
                                    resolvePrimaryLocationId(nextLocationIds, values.primary_location_id)
                                  );
                                  const allowed = new Set(resolveLocationIds(nextLocationIds));
                                  setFieldValue(
                                    "selected_department_ids",
                                    (values.selected_department_ids || []).filter((departmentId) => {
                                      const department = allDepartmentOptions.find(
                                        (item) => Number(item.id) === Number(departmentId)
                                      );
                                      return !department || allowed.has(Number(department.location_id));
                                    })
                                  );
                                }}
                                options={locationList.map((location) => ({
                                  value: Number(location.id),
                                  label: location.name,
                                }))}
                              />
                            </AppField>
                          ) : null}
                          {locationList.length > 1 ? (
                            <AppField
                              label={t("team.primary_location") || "Standard-Standort"}
                              help={t("team.primary_location_help")
                                || "Einstiegsstandort in Web und App."}
                            >
                              <AppSelect
                                value={resolvePrimaryLocationId(
                                  values.selected_location_ids,
                                  values.primary_location_id
                                )}
                                disabled={
                                  !canManageLocations
                                  || (values.selected_location_ids || []).length === 0
                                }
                                placeholder={t("team.primary_location_placeholder") || "Standort auswählen"}
                                onChange={(event) => setFieldValue(
                                  "primary_location_id",
                                  Number(event.target.value)
                                )}
                                options={(values.selected_location_ids || [])
                                  .map((locationId) => locationList.find(
                                    (location) => Number(location.id) === Number(locationId)
                                  ))
                                  .filter(Boolean)
                                  .map((location) => ({
                                    value: Number(location.id),
                                    label: location.name,
                                  }))}
                              />
                            </AppField>
                          ) : null}
                          <AppField
                            label={t("registration.skills") || "Abteilungen"}
                            required
                            span={2}
                            error={touched.selected_department_ids ? errors.selected_department_ids : undefined}
                          >
                            <AppSelect
                              multiple
                              searchable
                              showSelectAll
                              searchPlaceholder="Abteilung suchen"
                              value={values.selected_department_ids}
                              onChange={(event) => setFieldValue(
                                "selected_department_ids",
                                event.target.value.map(Number)
                              )}
                              options={buildDepartmentSelectOptions(values.selected_location_ids)}
                              renderValue={(_selectedValues, selectedOptions) => (
                                <span className="invite-select-chips">
                                  {selectedOptions.map((option) => (
                                    <AppChip key={option.value} variant="neutral" appearance="outlined" size="sm">
                                      {option.displayLabel}
                                    </AppChip>
                                  ))}
                                </span>
                              )}
                              placeholder="Abteilungen auswählen"
                              invalid={Boolean(touched.selected_department_ids && errors.selected_department_ids)}
                            />
                          </AppField>
                        </AppFieldGrid>
                    </section>
                </form>
              </AppDialogBody>
              <AppDialogFooter>
                <AppButton
                  variant="neutral"
                  size="md"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t("button.cancel") || "Abbrechen"}
                </AppButton>
                <AppButton
                  variant="primary"
                  size="md"
                  onClick={onSaveClick}
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting ? "Wird eingeladen…" : (t("registration.invite_title") || "Einladen")}
                </AppButton>
              </AppDialogFooter>
            </AppDialog>
          );
        }}
      </Formik>
  );
};

export default InviteDialog;
